import { copyText } from '@npc/platform'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Check, Clipboard, KeyRound, Link2, Pause, Play, Plus, RotateCw, Square, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useProjects } from '../../app/queries'
import {
  actOnRemoteAISandbox,
  createRemoteAIInvitation,
  createRemoteAISandbox,
  getRemoteAISettings,
  listRemoteAISandboxes,
  revokeRemoteAI,
  revokeRemoteAIInvitation,
  updateRemoteAIStatus,
  type RemoteAIInvitationBundle,
} from '../../app/remote-ai'
import { Card, CardTitle } from '../../components/card'
import { Page, PageHeader, SectionTitle } from '../../components/page'
import { RowSkeleton, ScreenLoading } from '../../components/state'
import { t } from '../../copy'
import './bridge.css'

const inputClass =
  'h-[40px] w-full rounded-npc-button border border-npc-border bg-npc-panel px-[12px] font-[inherit] text-npc-body-sm text-npc-ink outline-none focus:border-npc-border-hover'
const buttonClass =
  'inline-flex h-[36px] cursor-pointer items-center justify-center gap-[7px] rounded-npc-button border border-npc-border bg-npc-panel px-[12px] font-[inherit] text-npc-body-sm text-npc-ink-medium hover:border-npc-border-hover hover:text-npc-ink-strong disabled:cursor-not-allowed disabled:opacity-50'

/**
 * 有效期四档。**这是界面上唯一一份档位表**——表单与身份卡共用它。
 *
 * 两份的话，同一个 `90d` 会在一屏显示「90 天」、另一屏显示「90 天身份」，
 * 而那正是 CLAUDE.md 反复点名的「同一个东西两种说法」。
 */
const DURATIONS = [
  ['30d', '30 天'],
  ['90d', '90 天'],
  ['180d', '180 天'],
  ['long_term', '长期'],
] as const

type RemoteAIDuration = (typeof DURATIONS)[number][0]

/** 档位机器码 → 中文。认不出就回 null，让调用方决定怎么退化，不要在这儿编一个。 */
function durationLabel(value: string | null | undefined): string | null {
  return DURATIONS.find(([key]) => key === value)?.[1] ?? null
}

export function BridgeScreen() {
  const projects = useProjects()
  const queryClient = useQueryClient()
  const availableProjects = useMemo(
    () => (projects.data?.items ?? []).filter((project) => !project.is_builtin),
    [projects.data?.items],
  )
  const [projectId, setProjectId] = useState('')
  const [name, setName] = useState('')
  const [provider, setProvider] = useState('')
  const [model, setModel] = useState('')
  const [duration, setDuration] = useState<RemoteAIDuration>('90d')
  const [invitation, setInvitation] = useState<RemoteAIInvitationBundle | null>(null)
  const [copied, setCopied] = useState<'link' | 'prompt' | null>(null)
  const inviteFormRef = useRef<HTMLDivElement | null>(null)
  const inviteNameRef = useRef<HTMLInputElement | null>(null)

  const focusInviteForm = () => {
    inviteFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // 提交钮空表单时 disabled，不能聚焦；落到身份名称输入框。
    window.setTimeout(() => inviteNameRef.current?.focus(), 120)
  }

  useEffect(() => {
    if (!projectId && availableProjects[0]) setProjectId(availableProjects[0].id)
  }, [availableProjects, projectId])

  const settings = useQuery({
    queryKey: ['remote-ai', projectId],
    queryFn: () => getRemoteAISettings(projectId),
    enabled: Boolean(projectId),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['remote-ai', projectId] })
  const create = useMutation({
    mutationFn: () => createRemoteAIInvitation(projectId, { name, provider, model, duration }),
    onSuccess: (bundle) => {
      setInvitation(bundle)
      setCopied(null)
      setName('')
      void refresh()
    },
  })
  const update = useMutation({
    mutationFn: ({ identityId, status }: { identityId: string; status: 'active' | 'paused' }) =>
      updateRemoteAIStatus(projectId, identityId, status),
    onSuccess: () => void refresh(),
  })
  const revoke = useMutation({
    mutationFn: (identityId: string) => revokeRemoteAI(projectId, identityId),
    onSuccess: () => void refresh(),
  })
  const revokeInvitation = useMutation({
    mutationFn: (invitationId: string) => revokeRemoteAIInvitation(projectId, invitationId),
    onSuccess: () => void refresh(),
  })

  if (projects.isPending) return <ScreenLoading shape="detail" />

  return (
    <Page className="bridge-theme">
      <PageHeader
        title="AI 与 API"
        subtitle="给每个 AI 一把项目 API；有效期内可开发和快速发布，随时暂停或删除"
        className="mb-npc-card"
      />

      <Card className="mb-npc-card">
        <div className="flex flex-wrap items-end justify-between gap-npc-grid">
          <div className="min-w-[240px] flex-1">
            <label className="mb-[7px] block text-npc-caption text-npc-ink-soft" htmlFor="remote-ai-project">
              项目
            </label>
            <select
              id="remote-ai-project"
              className={inputClass}
              value={projectId}
              onChange={(event) => {
                setProjectId(event.target.value)
                setInvitation(null)
              }}
            >
              {availableProjects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
          <div className="text-npc-caption text-npc-ink-softer">
            {settings.data?.identities.length ?? 0} 个身份 · {settings.data?.invitations.length ?? 0} 个待兑换邀请
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-npc-card max-[900px]:grid-cols-1">
        <Card>
          <div ref={inviteFormRef} id="remote-ai-invite-form" data-testid="remote-ai-invite-form">
          <CardTitle className="mb-npc-grid flex items-center gap-[8px]">
            <Plus size={16} aria-hidden /> 新增远程 AI
          </CardTitle>
          <form
            className="grid gap-npc-grid-sm"
            onSubmit={(event) => {
              event.preventDefault()
              setInvitation(null)
              create.mutate()
            }}
          >
            <Field label="身份名称">
              <input ref={inviteNameRef} className={inputClass} value={name} onChange={(event) => setName(event.target.value)} placeholder="例如 Grok Bot" required />
            </Field>
            <div className="grid grid-cols-2 gap-npc-grid-sm">
              <Field label="Provider">
                <input className={inputClass} value={provider} onChange={(event) => setProvider(event.target.value)} placeholder="xai" required />
              </Field>
              <Field label="模型 / Agent">
                <input className={inputClass} value={model} onChange={(event) => setModel(event.target.value)} placeholder="grok-4.6" required />
              </Field>
            </div>
            {/* 四档一排的分段选择。
                **一、不能包在 Field 里。** Field 渲染的是 `<label>`，而 `<button>`
                在 HTML 里是 labelable 元素——被 label 包住时，它的可访问名字取的是
                label 的文字。四个按钮于是全都念成「有效期」，读屏的人根本分不出
                自己选的是哪一档。用例最先撞到的就是这个：文字明明是「30 天」，
                按名字却找不到它。所以这里用 role=group + aria-label，不用 label。
                **二、不用 grid-cols-4。** 四个等宽格子在 390px 上会把「长期」挤成
                两行。flex + flex-1 + 最小宽度，窄屏自己换行。 */}
            <div>
              <span className="mb-[7px] block text-npc-caption text-npc-ink-soft">有效期</span>
              <div role="group" aria-label="有效期" className="flex flex-wrap gap-[6px]">
                {DURATIONS.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={duration === value}
                    className={`${buttonClass} min-w-[62px] flex-1 px-[8px] ${duration === value ? 'br-seg-active' : ''}`}
                    onClick={() => setDuration(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {create.error ? <InlineError error={create.error} /> : null}
            <button
              type="submit"
              className="mt-[2px] inline-flex h-[40px] cursor-pointer items-center justify-center gap-[8px] rounded-npc-button border-none bg-npc-green px-[14px] font-[inherit] text-npc-section-sm font-semibold text-npc-panel transition-[filter,transform] hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!projectId || !name || !provider || !model || create.isPending}
            >
              <KeyRound size={16} aria-hidden /> 生成一次性邀请链接
            </button>
          </form>
          </div>
        </Card>

        <Card tone={invitation ? 'dark' : 'warm'}>
          <CardTitle className="mb-npc-grid">直接交给远程 AI</CardTitle>
          {invitation ? (
            <>
              <div className="text-npc-caption opacity-70">完整接入提示词 · 复制整段发送</div>
              <pre className="mt-[8px] max-h-[320px] overflow-auto whitespace-pre-wrap break-words rounded-npc-code bg-black/20 p-npc-grid-sm text-npc-meta leading-[1.7]">
                {invitation.prompt}
              </pre>
              <button
                type="button"
                className={`${buttonClass} mt-[8px] ${copied === 'prompt' ? 'border-npc-green-border text-npc-green-deep' : ''}`}
                onClick={() => {
                  // 复制不成就不翻成「已复制」——按钮说的话必须是真的。
                  void copyText(invitation.prompt).then((r) => { if (r.ok) setCopied('prompt') })
                }}
              >
                {copied === 'prompt' ? <Check size={15} aria-hidden /> : <Clipboard size={15} aria-hidden />}
                {copied === 'prompt' ? '完整提示词已复制' : '复制完整提示词'}
              </button>
              <div className="mt-npc-grid-sm rounded-npc-code bg-black/20 p-npc-grid-sm">
                <div className="mb-[7px] text-npc-caption opacity-70">24 小时有效 · 仅可激活一次</div>
                <code className="break-all text-npc-meta leading-[1.7]">{invitation.invite_url}</code>
              </div>
              <button
                type="button"
                className={`${buttonClass} mt-npc-grid-sm ${copied === 'link' ? 'border-npc-green-border text-npc-green-deep' : ''}`}
                onClick={() => {
                  void copyText(invitation.invite_url).then((r) => { if (r.ok) setCopied('link') })
                }}
              >
                {copied === 'link' ? <Check size={15} aria-hidden /> : <Link2 size={15} aria-hidden />}
                {copied === 'link' ? '邀请链接已复制' : '复制一次性邀请链接'}
              </button>
            </>
          ) : (
            <div className="flex min-h-[220px] items-center justify-center text-center text-npc-body-sm leading-[1.7] text-npc-ink-soft">
              生成后复制完整提示词。远程 AI 只保存一把项目 API，无需网页登录或另领仓库权限；API key 只显示一次。
            </div>
          )}
        </Card>
      </div>

      {/* 待兑换的邀请。**后端一直在返 settings.invitations，界面只数了个数。**
          后果是：管理员发出去一张邀请、关掉页面，就再也看不到它——谁的、
          什么时候过期、到底发没发出去过，一条线索都没有。而邀请在兑换前是
          bearer secret，「记不清有没有发过」正是最不该出现的那种状态。 */}
      {settings.data && settings.data.invitations.length > 0 ? (
        <section className="mt-npc-card">
          <SectionTitle className="mb-npc-grid-sm">还没被领走的邀请</SectionTitle>
          <div className="grid gap-npc-grid-sm">
            {settings.data.invitations.map((invitation) => (
              <Card key={invitation.id} padding="sm" tone="warm" data-testid={`remote-ai-invitation-${invitation.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-npc-grid-sm">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-[8px]">
                      <strong className="text-npc-section-sm text-npc-ink-strong">{invitation.name}</strong>
                      <span className="rounded-npc-tag-sm bg-npc-amber-bg px-[7px] py-[2px] text-npc-meta text-npc-amber-ink">
                        等着被领走
                      </span>
                    </div>
                    <div className="mt-[3px] text-npc-meta text-npc-ink-softer">
                      {invitation.provider} / {invitation.model}
                      {' · '}
                      {durationLabel(invitation.duration) ?? '限期'}
                      {' · '}
                      发出 {formatDate(invitation.created_at)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-npc-grid-sm">
                    <span className="text-npc-meta text-npc-ink-softer">
                      {formatDate(invitation.expires_at)} 前有效
                    </span>
                    {/* 作废按钮。原来这张卡片是只读的，唯一的补救是等它过期——
                        而邀请在被领走之前就是一把 bearer secret，「发错了人」
                        与「链接落进了聊天记录」都要等一整天才失效。 */}
                    <button
                      type="button"
                      className={`${buttonClass} hover:border-npc-red-border hover:text-npc-red-ink`}
                      disabled={revokeInvitation.isPending}
                      data-testid={`remote-ai-invitation-revoke-${invitation.id}`}
                      onClick={() => {
                        if (window.confirm(t('development_task.remote_ai.invitation.revoke_confirm'))) {
                          revokeInvitation.mutate(invitation.id)
                        }
                      }}
                    >
                      <Trash2 size={14} aria-hidden />
                      {/* 「正在作废」只挂在真正在飞的那一张上。整排一起显示的话，
                          管理员会以为自己一下子撤掉了所有邀请。 */}
                      {revokeInvitation.isPending && revokeInvitation.variables === invitation.id
                        ? t('development_task.remote_ai.invitation.revoking')
                        : t('development_task.remote_ai.invitation.revoke')}
                    </button>
                  </div>
                </div>
                {/* 刻意不提供「再看一遍链接」。能再看一遍的一次性 secret 就不是一次性的了。 */}
                <p className="m-0 mt-npc-grid-sm text-npc-caption leading-[1.7] text-npc-ink-soft">
                  {t('development_task.remote_ai.invitation.pending_hint')}
                </p>
                {/* 失败时说的不是原始错误，是「它还能被领走」——那才是管理员
                    下一步要知道的事。原始错误留在 network 面板里。 */}
                {revokeInvitation.error ? (
                  <p role="alert" className="m-0 mt-npc-grid-sm text-npc-caption leading-[1.7] text-npc-red-ink">
                    {t('development_task.remote_ai.invitation.revoke_failed')}
                  </p>
                ) : null}
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-npc-card">
        <SectionTitle className="mb-npc-grid-sm">已接入的 AI</SectionTitle>
        {settings.isPending ? <RowSkeleton rows={3} /> : settings.error ? <InlineError error={settings.error} /> : null}
        <div className="grid gap-npc-grid-sm">
          {settings.data?.executor_bootstrap?.attempted ? (
            <Card padding="sm">
              {/* 本机执行器开通的结果。**后端注释明写：失败时这里是唯一
                  看得见原因的地方**——身份没建出来本身没有任何线索。
                  在这之前前端零命中，等于开通失败完全静默。 */}
              <div className="text-npc-section-sm text-npc-ink-strong">本机执行器</div>
              {settings.data.executor_bootstrap.ok ? (
                <>
                  <div className="mt-[3px] text-npc-meta text-npc-ink-softer">
                    身份已就绪{settings.data.executor_bootstrap.reused ? '（复用了原来那把）' : '（这次新签的）'}
                    {settings.data.executor_bootstrap.fingerprint
                      ? ` · ${shortFingerprint(settings.data.executor_bootstrap.fingerprint)}`
                      : ''}
                    {settings.data.executor_bootstrap.expires_at
                      ? ` · 到期 ${formatDate(settings.data.executor_bootstrap.expires_at)}`
                      : ''}
                  </div>
                  <div className={`mt-[3px] text-npc-meta ${settings.data.executor_bootstrap.environment_ready ? 'text-npc-green-ink' : 'text-npc-red-ink'}`}>
                    {settings.data.executor_bootstrap.environment_ready
                      ? `开发环境运行中${settings.data.executor_bootstrap.environment_id ? ` · ${settings.data.executor_bootstrap.environment_id.slice(0, 8)}` : ''}`
                      : `开发环境未就绪：${settings.data.executor_bootstrap.environment_error || '等待平台调度器'}`}
                  </div>
                </>
              ) : (
                <div className="mt-[3px] text-npc-meta text-npc-red-ink">
                  没开通成：{settings.data.executor_bootstrap.reason || '原因没记下来'}
                </div>
              )}
            </Card>
          ) : null}
          {settings.data?.identities.map((identity) => (
            <Card key={identity.id} padding="sm" data-testid={`remote-ai-${identity.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-npc-grid-sm">
                <div className="flex min-w-0 items-center gap-[10px]">
                  <span className="flex size-[34px] shrink-0 items-center justify-center rounded-npc-button bg-npc-surface-warm text-npc-ink-medium">
                    <Bot size={17} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-[8px]">
                      <strong className="text-npc-section-sm text-npc-ink-strong">{identity.name}</strong>
                      <Status status={identity.status} />
                    </div>
                    <div className="mt-[3px] text-npc-meta text-npc-ink-softer">一把项目 API · 开发与快速发布</div>
                    <div className="mt-[3px] text-npc-meta text-npc-ink-softer font-mono">
                      {shortFingerprint(identity.fingerprint)}
                    </div>
                    {/* 到期与 allowed_modes 原来就在显示；这次补上的是**档位与来源**。
                        管理员建身份时选了有效期，之后却只看得到「哪天到期」——
                        而从一个绝对时刻反推不出当初选的是哪一档（长期身份没有到期时间，
                        30 天和 90 天在剩两天时长得一模一样）。 */}
                    <div className="mt-[3px] text-npc-meta text-npc-ink-softer">
                      {/* 档位为 null 的是 000158 之前建的老身份——**不替它猜**，
                          按有没有到期时间退化成一句真话。 */}
                      {durationLabel(identity.duration) ?? (identity.expires_at ? '限期' : '长期')}
                      {' · '}
                      {identity.redeemed_at ? '邀请兑换' : '平台开通'}
                      {' · '}
                      {identity.expires_at ? `到期 ${formatDate(identity.expires_at)}` : '不会过期'}
                      {identity.allowed_modes && identity.allowed_modes.length > 0
                        ? ` · 允许 ${identity.allowed_modes.join(' / ')}`
                        : ' · 没有开放任何发布方式'}
                    </div>
                  </div>
                </div>
                <div className="flex gap-[6px]">
                  {identity.status !== 'revoked' ? (
                    <button
                      type="button"
                      className={buttonClass}
                      disabled={update.isPending}
                      title={identity.status === 'active' ? '暂停' : '恢复'}
                      onClick={() => update.mutate({ identityId: identity.id, status: identity.status === 'active' ? 'paused' : 'active' })}
                    >
                      {identity.status === 'active' ? <Pause size={14} aria-hidden /> : <Play size={14} aria-hidden />}
                      {identity.status === 'active' ? '暂停' : '恢复'}
                    </button>
                  ) : null}
                  {identity.status !== 'revoked' ? (
                    <button
                      type="button"
                      className={`${buttonClass} hover:border-npc-red-border hover:text-npc-red-ink`}
                      disabled={revoke.isPending}
                      title="删除项目 API"
                      onClick={() => {
                        if (window.confirm(`删除 ${identity.name} 的项目 API？删除后立即失效且不能恢复。`)) revoke.mutate(identity.id)
                      }}
                    >
                      <Trash2 size={14} aria-hidden /> 删除
                    </button>
                  ) : null}
                </div>
              </div>
              <CapabilityScope apis={identity.apis} />
              <IdentitySandboxes projectId={projectId} identityId={identity.id} identityStatus={identity.status} />
            </Card>
          ))}
          {settings.data && settings.data.identities.length === 0 ? (
            <RemoteAISandboxEmpty onInvite={focusInviteForm} />
          ) : null}
        </div>
      </section>

    </Page>
  )
}

/**
 * 「这把 key 能干什么、够不到什么」。
 *
 * 原来这儿是把 identity.apis 那十几条原始 endpoint 直接铺成一排 code 标签。
 * 那是**给机器看的东西摆在了第一屏**——CLAUDE.md §2 第 2 条要求第一屏说人话，
 * 技术细节收进第二层。
 *
 * 更要紧的是：一排 endpoint 回答不了管理员真正在问的那个问题——
 * 「这把 key 会不会碰到我的服务器 / 仓库 / main」。那个答案不在 endpoint 列表里，
 * 它在身份合同 v2 的那几个布尔值上，而这一段把它直接说出来。
 */
function CapabilityScope({ apis }: { apis: string[] }) {
  return (
    <div className="mt-npc-grid-sm border-t border-npc-border pt-npc-grid-sm">
      <div className="mb-[7px] text-npc-caption text-npc-ink-soft">这把项目 API 能做什么</div>
      <ul className="m-0 grid list-none gap-[4px] p-0 text-npc-meta leading-[1.7] text-npc-ink-medium">
        <li>读取这个项目的源码和自己的任务</li>
        <li>提交候选并走 AI 快速发布，无需另找管理员开仓库权限</li>
        <li>在所选有效期内持续使用；暂停或删除后全部失效</li>
      </ul>
      <div className="mb-[7px] mt-npc-grid-sm text-npc-caption text-npc-ink-soft">碰不到什么</div>
      <ul className="m-0 grid list-none gap-[4px] p-0 text-npc-meta leading-[1.7] text-npc-ink-medium">
        <li>服务器、SSH 和仓库长期密码</li>
        <li>其他项目或其他 AI 的身份</li>
        <li>直接覆盖 main；平台只接收并发布它提交的 exact candidate</li>
      </ul>
      <details className="mt-npc-grid-sm">
        <summary className="cursor-pointer text-npc-caption text-npc-ink-soft">看具体开了哪几个接口</summary>
        <div className="mt-[8px] flex flex-wrap gap-[6px]">
          {apis.map((endpoint) => (
            <code key={endpoint} className="rounded-npc-code bg-npc-surface-warm px-[7px] py-[3px] text-npc-meta text-npc-ink-soft">
              {endpoint}
            </code>
          ))}
        </div>
      </details>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block"><span className="mb-[7px] block text-npc-caption text-npc-ink-soft">{label}</span>{children}</label>
}

function InlineError({ error }: { error: unknown }) {
  return <p role="alert" className="m-0 text-npc-caption text-npc-red-ink">{error instanceof Error ? error.message : '操作没有完成'}</p>
}

function Status({ status }: { status: string }) {
  const label = status === 'active' ? '可用' : status === 'paused' ? '已暂停' : '已撤销'
  const tone = status === 'active' ? 'bg-npc-green-bg text-npc-green-deep' : status === 'paused' ? 'bg-npc-amber-bg text-npc-amber-ink' : 'bg-npc-red-bg text-npc-red-ink'
  return <span className={`rounded-npc-tag-sm px-[7px] py-[2px] text-npc-meta ${tone}`}>{label}</span>
}

function shortFingerprint(value: string): string {
  return value.length > 14 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value
}

function formatDate(value: string | null): string {
  if (!value) return '从未'
  return new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}


/**
 * 远程 AI 沙箱列表空态（P1）：深蓝玻璃氛围，对齐 AI 面顶栏的 blur 气质。
 * 动作不埋——CTA 滚回并聚焦已有的「生成一次性邀请链接」。
 * 不改钥匙 / 平台动手授权的空态。
 */
function RemoteAISandboxEmpty({ onInvite }: { onInvite: () => void }) {
  return (
    <div
      data-testid="remote-ai-sandbox-empty"
      className="rounded-npc-card border border-[rgba(110,150,210,0.32)] bg-[rgba(16,28,48,0.78)] px-[18px] py-[22px] backdrop-blur-[12px]" /* npc-token-allow: bridge P1 氛围空态——深蓝玻璃，对齐 AI 面顶栏 blur，整站 token 无此档 */
    >
      <div className="flex items-start gap-[12px]">
        <span className="mt-[2px] flex size-[36px] shrink-0 items-center justify-center rounded-npc-button border border-[rgba(110,150,210,0.28)] bg-[rgba(40,70,120,0.35)] text-[rgba(170,200,240,0.92)]" /* npc-token-allow: bridge P1 氛围空态图标底 */>
          <Bot size={18} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-npc-section-sm font-medium text-npc-ink-strong">还没有远程 AI 身份</div>
          <p className="m-0 mt-[6px] text-npc-body-sm leading-[1.65] text-npc-ink-soft">
            生成一次性邀请链接，让外部 AI 换项目身份进来；本机执行器不受影响。
          </p>
          <button
            type="button"
            className="mt-[12px] inline-flex h-[36px] cursor-pointer items-center justify-center gap-[7px] rounded-npc-button border border-[rgba(110,150,210,0.4)] bg-[rgba(40,70,120,0.45)] px-[12px] font-[inherit] text-npc-body-sm text-[rgba(200,220,245,0.95)] hover:border-[rgba(140,175,230,0.55)]" /* npc-token-allow: bridge P1 氛围空态 CTA */
            onClick={onInvite}
            data-testid="remote-ai-sandbox-empty-cta"
          >
            <KeyRound size={15} aria-hidden /> 去生成一次性邀请链接
          </button>
        </div>
      </div>
    </div>
  )
}

function IdentitySandboxes({ projectId, identityId, identityStatus }: { projectId: string; identityId: string; identityStatus: string }) {
  const queryClient = useQueryClient()
  const [repo, setRepo] = useState('')
  const sandboxes = useQuery({
    queryKey: ['remote-ai-sandboxes', projectId, identityId],
    queryFn: () => listRemoteAISandboxes(projectId, identityId),
    enabled: Boolean(projectId && identityId),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['remote-ai-sandboxes', projectId, identityId] })
  const create = useMutation({
    mutationFn: () => createRemoteAISandbox(projectId, identityId, { repo }),
    onSuccess: () => {
      setRepo('')
      void refresh()
    },
  })
  const act = useMutation({
    mutationFn: ({ sandboxId, action }: { sandboxId: string; action: 'start' | 'stop' | 'reconnect' | 'rebuild' | 'destroy' }) =>
      actOnRemoteAISandbox(projectId, identityId, sandboxId, action),
    onSuccess: () => void refresh(),
  })
  const live = (sandboxes.data?.sandboxes ?? []).filter((item) => item.state !== 'destroyed')
  const canManage = identityStatus !== 'revoked'

  return (
    <div className="mt-npc-grid-sm border-t border-npc-border pt-npc-grid-sm">
      <div className="mb-[7px] text-npc-caption text-npc-ink-soft">持久沙箱</div>
      {sandboxes.error ? <InlineError error={sandboxes.error} /> : null}
      {live.map((item) => (
        <div key={item.id} className="mb-[8px] rounded-npc-code bg-npc-surface-warm p-npc-grid-sm" data-testid={`remote-ai-sandbox-${item.id}`}>
          <div className="text-npc-meta text-npc-ink-medium">
            {item.state} · {item.cpu} CPU · {item.memory} · {item.disk} · {item.network} · {item.repo}
          </div>
          {item.sync_error ? <p className="m-0 mt-[4px] text-npc-caption text-npc-red-ink">{item.sync_error}</p> : null}
          {canManage ? (
            <div className="mt-[8px] flex flex-wrap gap-[6px]">
              <button type="button" className={buttonClass} disabled={act.isPending} onClick={() => act.mutate({ sandboxId: item.id, action: 'start' })}>启动</button>
              <button type="button" className={buttonClass} disabled={act.isPending} onClick={() => act.mutate({ sandboxId: item.id, action: 'stop' })}>
                <Square size={14} aria-hidden /> 停止
              </button>
              <button type="button" className={buttonClass} disabled={act.isPending} onClick={() => act.mutate({ sandboxId: item.id, action: 'reconnect' })}>重连</button>
              <button type="button" className={buttonClass} disabled={act.isPending} onClick={() => act.mutate({ sandboxId: item.id, action: 'rebuild' })}>
                <RotateCw size={14} aria-hidden /> 重建
              </button>
              <button
                type="button"
                className={`${buttonClass} hover:border-npc-red-border hover:text-npc-red-ink`}
                disabled={act.isPending}
                onClick={() => {
                  if (window.confirm('销毁这个沙箱？工作区卷会一并删除。')) act.mutate({ sandboxId: item.id, action: 'destroy' })
                }}
              >
                销毁
              </button>
            </div>
          ) : null}
        </div>
      ))}
      {canManage ? (
        <form
          className="mt-[8px] flex flex-wrap items-end gap-[8px]"
          onSubmit={(event) => {
            event.preventDefault()
            create.mutate()
          }}
        >
          <label className="min-w-[180px] flex-1">
            <span className="mb-[7px] block text-npc-caption text-npc-ink-soft">仓库</span>
            <input className={inputClass} value={repo} onChange={(event) => setRepo(event.target.value)} placeholder="arron/npc" required />
          </label>
          <button type="submit" className={buttonClass} disabled={!repo || create.isPending}>创建沙箱</button>
        </form>
      ) : null}
      {create.error ? <InlineError error={create.error} /> : null}
      {act.error ? <InlineError error={act.error} /> : null}
    </div>
  )
}
